import { Box, SxProps, Theme } from '@evaneos/b2b-ui';
import { ReactNode, useEffect, useState } from 'react';

type SlideAndFadeProps = {
    open: boolean;
    children: ReactNode;
    duration?: number;
    direction?: 'up' | 'down' | 'left' | 'right';
    distance?: number;
    sx?: SxProps<Theme>;
};

export const SlideAndFade = ({
    open,
    children,
    duration = 400,
    direction = 'up',
    distance = 50,
    sx = {},
}: SlideAndFadeProps) => {
    const [shouldRender, setShouldRender] = useState(open);
    const [isClosing, setIsClosing] = useState(false);

    const [prevOpen, setPrevOpen] = useState(open);
    if (prevOpen !== open) {
        setPrevOpen(open);
        if (open) {
            setShouldRender(true);
            setIsClosing(false);
        } else if (shouldRender) {
            setIsClosing(true);
        }
    }

    useEffect(() => {
        if (isClosing) {
            const timer = window.setTimeout(() => {
                setShouldRender(false);
                setIsClosing(false);
            }, duration);

            return () => {
                window.clearTimeout(timer);
            };
        }
    }, [isClosing, duration]);

    if (!shouldRender) {
        return null;
    }

    const getTransform = (isClosing: boolean) => {
        const transforms: Record<string, { from: string; to: string }> = {
            up: {
                from: `translateY(${distance}px)`,
                to: 'translateY(0px)',
            },
            down: {
                from: `translateY(-${distance}px)`,
                to: 'translateY(0px)',
            },
            left: {
                from: `translateX(${distance}px)`,
                to: 'translateX(0px)',
            },
            right: {
                from: `translateX(-${distance}px)`,
                to: 'translateX(0px)',
            },
        };

        return isClosing ? transforms[direction].from : transforms[direction].to;
    };

    return (
        <Box
            sx={{
                ...sx,
                animation: isClosing
                    ? `slideOut ${duration}ms ease-in both`
                    : `slideIn ${duration}ms ease-in both`,
                '@keyframes slideIn': {
                    from: {
                        transform: getTransform(true),
                        opacity: 0,
                    },
                    to: {
                        transform: getTransform(false),
                        opacity: 1,
                    },
                },
                '@keyframes slideOut': {
                    from: {
                        transform: getTransform(false),
                        opacity: 1,
                    },
                    to: {
                        transform: getTransform(true),
                        opacity: 0,
                    },
                },
            }}
        >
            {children}
        </Box>
    );
};
